import { useEffect, useState } from "react";
import { Link } from "react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import { z } from "zod";
import { ArrowLeft, Mail, MailCheck } from "lucide-react";
import { authService, STAFF_PASSWORD_MIN_LENGTH } from "@/services/authService";
import { Alert, Button, Input } from "@/components/ui";
import { AuthShell } from "@/components/common";
import { getApiErrorMessage } from "@/lib/errorMessages";

function createForgotSchema(t: (k: string) => string) {
  return z.object({
    email: z.email({ error: t("invalidEmail") }),
  });
}

type ForgotForm = z.infer<ReturnType<typeof createForgotSchema>>;

export default function ForgotPasswordPage() {
  const { t } = useTranslation("auth");
  const { t: tCommon } = useTranslation("common");
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [requestError, setRequestError] = useState<string | null>(null);

  const schema = createForgotSchema(t);
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<ForgotForm>({
    resolver: zodResolver(schema),
  });

  const emailWatch = watch("email");
  useEffect(() => {
    setRequestError(null);
  }, [emailWatch]);

  const onSubmit = async (data: ForgotForm) => {
    try {
      setRequestError(null);
      setIsLoading(true);
      await authService.forgotPassword(data.email.trim().toLowerCase());
      setSubmitted(true);
      toast.success(t("forgotPasswordSuccessToast"));
    } catch (err) {
      setRequestError(getApiErrorMessage(err, "forgotPasswordFailed"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthShell
      footer={<p>{tCommon("copyright", { year: new Date().getFullYear() })}</p>}
    >
      <div className="mb-8">
        <h1 className="text-2xl font-semibold tracking-tight text-surface-900">
          {t("forgotPasswordTitle")}
        </h1>
        <p className="mt-1.5 text-sm text-surface-500">
          {t("forgotPasswordSubtitle")}
        </p>
      </div>

      {submitted ? (
        <div className="flex items-start gap-3 rounded-lg border border-success-200 bg-success-50 px-4 py-3">
          <MailCheck className="mt-0.5 h-4 w-4 shrink-0 text-success-600" />
          <p className="text-sm text-success-800">
            {t("forgotPasswordCheckEmail")}
          </p>
        </div>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <Input
            {...register("email")}
            type="email"
            label={t("email")}
            placeholder={t("emailPlaceholder")}
            error={errors.email?.message}
            autoComplete="email"
            leftIcon={<Mail className="h-4 w-4" />}
          />
          <p className="text-[13px] leading-relaxed text-surface-500">
            {t("forgotPasswordRulesReminder", {
              min: STAFF_PASSWORD_MIN_LENGTH,
            })}
          </p>
          {requestError ? <Alert variant="error">{requestError}</Alert> : null}
          <Button
            type="submit"
            className="w-full"
            size="lg"
            isLoading={isLoading}
          >
            {t("forgotPasswordSubmit")}
          </Button>
        </form>
      )}

      <div className="mt-6">
        <Link
          to="/login"
          className="inline-flex items-center gap-1.5 text-[13px] font-medium text-primary-600 hover:text-primary-700"
        >
          <ArrowLeft className="h-4 w-4" />
          {t("backToLogin")}
        </Link>
      </div>
    </AuthShell>
  );
}
